// src/services/orders/orderService.js
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  getDoc, 
  getDocs,
  query, 
  where, 
  orderBy, 
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase';

const ORDERS_COLLECTION = 'orders';

export class OrderService {
  // Create a new order
  static async createOrder(orderData) {
    try {
      const order = {
        // Order identification
        orderNumber: this.generateOrderNumber(),
        status: 'pending', // pending, processing, shipped, delivered, cancelled
        paymentStatus: 'pending', // pending, processing, completed, failed, refunded

        // UID of the authenticated user who placed the order (null for guests)
        userId: orderData.userId || null,

        // Customer information
        customer: {
          email: orderData.customer.email,
          firstName: orderData.customer.firstName,
          lastName: orderData.customer.lastName,
          phone: orderData.customer.phone || ''
        },
        
        // Shipping address
        shippingAddress: {
          firstName: orderData.shippingAddress.firstName,
          lastName: orderData.shippingAddress.lastName,
          address: orderData.shippingAddress.address,
          city: orderData.shippingAddress.city,
          province: orderData.shippingAddress.province,
          postalCode: orderData.shippingAddress.postalCode,
          country: 'South Africa'
        },
        
        // Order items
        items: orderData.items.map(item => ({
          id: item.id,
          name: item.name,
          price: item.price,
          priceValue: item.priceValue,
          quantity: item.quantity,
          category: item.category,
          type: item.type,
          image: item.image,
          subtotal: item.priceValue * item.quantity
        })),
        
        // Order totals
        subtotal: orderData.totals.subtotal,
        shipping: orderData.totals.shipping || 0,
        tax: orderData.totals.tax || 0,
        total: orderData.totals.total,
        currency: 'ZAR',
        
        // Payment information
        payment: {
          method: orderData.payment?.method || 'payfast',
          transactionId: null,
          paymentDate: null,
          paymentReference: null
        },
        
        // Tracking
        tracking: {
          trackingNumber: null,
          courier: null,
          estimatedDelivery: null
        },
        
        // Notes
        notes: orderData.notes || '',
        adminNotes: '',
        
        // Timestamps
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, ORDERS_COLLECTION), order);
      
      return {
        id: docRef.id,
        ...order,
        createdAt: new Date(),
        updatedAt: new Date()
      };
    } catch (error) {
      console.error('Error creating order:', error);
      throw error;
    }
  }

  // Get order by ID
  static async getOrderById(orderId) {
    try {
      const docRef = doc(db, ORDERS_COLLECTION, orderId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          ...data,
          createdAt: data.createdAt?.toDate?.() || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || new Date()
        };
      } else {
        throw new Error('Order not found');
      }
    } catch (error) {
      console.error('Error fetching order:', error);
      throw error;
    }
  }

  // Get order by order number
  static async getOrderByNumber(orderNumber) {
    try {
      const q = query(
        collection(db, ORDERS_COLLECTION),
        where('orderNumber', '==', orderNumber)
      );
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate?.() || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || new Date()
        };
      } else {
        throw new Error('Order not found');
      }
    } catch (error) {
      console.error('Error fetching order by number:', error);
      throw error;
    }
  }

  // Update order status
  static async updateOrderStatus(orderId, status, adminNotes = '') {
    try {
      const docRef = doc(db, ORDERS_COLLECTION, orderId);
      const updates = {
        status,
        updatedAt: serverTimestamp()
      };

      if (adminNotes) {
        updates.adminNotes = adminNotes;
      }

      await updateDoc(docRef, updates);
      return await this.getOrderById(orderId);
    } catch (error) {
      console.error('Error updating order status:', error);
      throw error;
    }
  }

  // Update payment status
  static async updatePaymentStatus(orderId, paymentData) {
    try {
      const docRef = doc(db, ORDERS_COLLECTION, orderId);
      const updates = {
        paymentStatus: paymentData.status,
        'payment.transactionId': paymentData.transactionId,
        'payment.paymentReference': paymentData.paymentReference,
        'payment.paymentDate': paymentData.paymentDate || serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      // If payment is completed, also update order status
      if (paymentData.status === 'completed') {
        updates.status = 'processing';
      } else if (paymentData.status === 'failed') {
        updates.status = 'cancelled';
      }

      await updateDoc(docRef, updates);
      return await this.getOrderById(orderId);
    } catch (error) {
      console.error('Error updating payment status:', error);
      throw error;
    }
  }

  // Add tracking information
  static async addTrackingInfo(orderId, trackingData) {
    try {
      const docRef = doc(db, ORDERS_COLLECTION, orderId);
      const updates = {
        'tracking.trackingNumber': trackingData.trackingNumber,
        'tracking.courier': trackingData.courier,
        'tracking.estimatedDelivery': trackingData.estimatedDelivery,
        status: 'shipped',
        updatedAt: serverTimestamp()
      };

      await updateDoc(docRef, updates);
      return await this.getOrderById(orderId);
    } catch (error) {
      console.error('Error adding tracking info:', error);
      throw error;
    }
  }

  // Get orders by customer email
  static async getOrdersByCustomer(email) {
    try {
      const q = query(
        collection(db, ORDERS_COLLECTION),
        where('customer.email', '==', email),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const orders = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        orders.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate?.() || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || new Date()
        });
      });
      
      return orders;
    } catch (error) {
      console.error('Error fetching customer orders:', error);
      throw error;
    }
  }

  // Get orders by authenticated user UID (for orders created after userId was added)
  static async getOrdersByUserId(uid) {
    try {
      const q = query(
        collection(db, ORDERS_COLLECTION),
        where('userId', '==', uid),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const orders = [];
      querySnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        orders.push({
          id: docSnap.id,
          ...data,
          createdAt: data.createdAt?.toDate?.() || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || new Date(),
        });
      });
      return orders;
    } catch (error) {
      console.error('Error fetching orders by userId:', error);
      throw error;
    }
  }

  // Get all orders (admin)
  static async getAllOrders(limitCount = 50) {
    try {
      const q = query(
        collection(db, ORDERS_COLLECTION),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const orders = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        orders.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate?.() || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || new Date()
        });
      });
      
      return orders.slice(0, limitCount);
    } catch (error) {
      console.error('Error fetching all orders:', error);
      throw error;
    }
  }

  // Generate unique order number
  static generateOrderNumber() {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `PY${timestamp}${random}`;
  }

  // Calculate order totals
  static calculateOrderTotals(cartItems, shippingCost = 0, taxRate = 0.15) {
    const subtotal = cartItems.reduce((total, item) => {
      return total + (item.priceValue * item.quantity);
    }, 0);

    const shipping = subtotal >= 1000 ? 0 : shippingCost; // Free shipping over R1000
    const taxableAmount = subtotal + shipping;
    const tax = 0; // VAT is included in product prices in SA
    const total = subtotal + shipping + tax;

    return {
      subtotal: parseFloat(subtotal.toFixed(2)),
      shipping: parseFloat(shipping.toFixed(2)),
      tax: parseFloat(tax.toFixed(2)),
      total: parseFloat(total.toFixed(2))
    };
  }

  // Validate order data
  static validateOrderData(orderData) {
    const errors = [];

    // Customer validation
    if (!orderData.customer?.email) {
      errors.push('Customer email is required');
    }
    if (!orderData.customer?.firstName) {
      errors.push('Customer first name is required');
    }
    if (!orderData.customer?.lastName) {
      errors.push('Customer last name is required');
    }

    // Shipping address validation
    if (!orderData.shippingAddress?.address) {
      errors.push('Shipping address is required');
    }
    if (!orderData.shippingAddress?.city) {
      errors.push('Shipping city is required');
    }
    if (!orderData.shippingAddress?.province) {
      errors.push('Shipping province is required');
    }
    if (!orderData.shippingAddress?.postalCode) {
      errors.push('Shipping postal code is required');
    }

    // Items validation
    if (!orderData.items || orderData.items.length === 0) {
      errors.push('Order must contain at least one item');
    }

    // Totals validation
    if (!orderData.totals || orderData.totals.total <= 0) {
      errors.push('Order total must be greater than 0');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // Customer: Cancel/delete a pending order
  static async cancelPendingOrder(orderId) {
    try {
      const order = await this.getOrderById(orderId);
      
      // Only allow cancellation of pending orders
      if (order.status !== 'pending') {
        throw new Error('Only pending orders can be cancelled');
      }

      // Delete the order
      const docRef = doc(db, ORDERS_COLLECTION, orderId);
      await deleteDoc(docRef);
      
      return { success: true, message: 'Order cancelled successfully' };
    } catch (error) {
      console.error('Error cancelling order:', error);
      throw error;
    }
  }

  // Customer: Mark order as complete/delivered
  static async markOrderAsComplete(orderId) {
    try {
      const order = await this.getOrderById(orderId);
      
      // Only allow marking as complete if order is shipped or processing
      if (order.status !== 'shipped' && order.status !== 'processing') {
        throw new Error('Only shipped or processing orders can be marked as complete');
      }

      const docRef = doc(db, ORDERS_COLLECTION, orderId);
      await updateDoc(docRef, {
        status: 'delivered',
        updatedAt: serverTimestamp()
      });
      
      return await this.getOrderById(orderId);
    } catch (error) {
      console.error('Error marking order as complete:', error);
      throw error;
    }
  }

  // Get order statistics
  static async getOrderStats() {
    try {
      const orders = await this.getAllOrders(1000); // Get recent orders for stats
      
      const stats = {
        total: orders.length,
        pending: orders.filter(o => o.status === 'pending').length,
        processing: orders.filter(o => o.status === 'processing').length,
        shipped: orders.filter(o => o.status === 'shipped').length,
        delivered: orders.filter(o => o.status === 'delivered').length,
        cancelled: orders.filter(o => o.status === 'cancelled').length,
        totalRevenue: orders
          .filter(o => o.paymentStatus === 'completed')
          .reduce((sum, o) => sum + o.total, 0),
        averageOrderValue: 0
      };

      if (stats.total > 0) {
        stats.averageOrderValue = stats.totalRevenue / stats.total;
      }

      return stats;
    } catch (error) {
      console.error('Error getting order stats:', error);
      throw error;
    }
  }
}